import { createApi, retry } from '@reduxjs/toolkit/query/react'
import config from '@/config'
import { axiosBaseQuery } from '../helpers'

// The switch operator: control plane for the AI voice agent (numbers,
// provisioning, call history). Staff-only surface while piloted.

const service = 'operator'

export interface VoicePhoneNumber {
  id: string
  e164: string
  provider: string
  organization_id: string
  serial: string | null
  venue_name: string | null
  elevenlabs_agent_id: string | null
  status: 'pending' | 'provisioning' | 'active' | 'released'
  call_mode: 'ai_first' | 'venue_first'
  venue_phone: string | null
  ring_seconds: number
  venue_hours_start: string | null
  venue_hours_end: string | null
  voice_id: string | null
  greeting: string | null
  personality: string | null
  /** free text the venue writes about itself; facts, not tone */
  venue_context: string | null
  language_default: string | null
  languages_extra: string[] | null
  // Numbers that reach this DID through a switchboard, so their caller ID is
  // that switchboard's line rather than the guest's. A call from one of these
  // makes the agent ask the guest for their number.
  switchboard_numbers: string[] | null
  /** the Twilio number sid: set on a purchased number, null on a virtual demo DID */
  provider_number_sid: string | null
  // Outbound calls: staff ringing a guest as the venue
  outbound_enabled: boolean
  outbound_international: boolean
  outbound_daily_cap_minutes: number
  outbound_allowed_uids: string[] | null
  /** the number the guest sees; null = this number */
  caller_id_e164: string | null
  recording_enabled: boolean
  /** what the guest hears before the bridge; null = the stock line */
  consent_text: string | null
  retention_days: number
  created_at: string
}

export interface VoiceCatalogueVoice {
  voice_id: string
  name: string
  labels: Record<string, string>
  preview_url: string | null
}

export interface VoiceCallHandlingUpdate {
  call_mode?: 'ai_first' | 'venue_first'
  venue_phone?: string | null
  ring_seconds?: number
  venue_hours_start?: string | null
  venue_hours_end?: string | null
  voice_id?: string | null
  greeting?: string | null
  personality?: string | null
  venue_context?: string | null
  language_default?: string | null
  languages_extra?: string[] | null
  switchboard_numbers?: string[] | null
  outbound_enabled?: boolean
  outbound_international?: boolean
  outbound_daily_cap_minutes?: number
  outbound_allowed_uids?: string[] | null
  caller_id_e164?: string | null
  recording_enabled?: boolean
  consent_text?: string | null
  retention_days?: number
}

export interface VoiceCallFigures {
  calls: number
  /** answered by the agent rather than the venue's own phone */
  answered_by_ai: number
  answered_by_venue: number
  /** reached a successful create_booking in the same call */
  with_booking: number
  /** seconds, across answered calls */
  talk_time_secs: number
}

export interface VoiceCallStats {
  from: string | null
  to: string | null
  range: VoiceCallFigures
  all_time: VoiceCallFigures
  /** the first call this venue ever took */
  first_call_at: string | null
}

export interface VoiceCall {
  id: string
  status: 'ringing' | 'answered' | 'completed' | 'failed'
  answered_by: 'venue' | 'ai' | null
  caller_id: string | null
  started_at: string
  answered_at: string | null
  ended_at: string | null
  duration_secs: number | null
  has_transcript: boolean
  /** an outbound call's own audio, still within retention */
  has_recording: boolean
  direction: 'inbound' | 'outbound'
  /** outbound only */
  mode: 'ring_me' | 'browser' | null
  to_number: string | null
  staff_number: string | null
  placed_by: string | null
  placed_by_name: string | null
  enquiry_id: string | null
  phone_number: { e164: string; venue_name: string | null }
}

export interface PlaceOutboundCallInput {
  org_id: string
  serial: string
  mode: 'ring_me' | 'browser'
  /** the guest's number, any sensible format */
  to: string
  /** ring_me: the number to ring the staff member on */
  staff_number?: string
  enquiry_id?: string
}

export interface PlaceOutboundCallResult {
  call: VoiceCall
  /** browser mode: presented to the SIP edge in X-Stampede-Call-Token */
  token?: string
  expires_at?: string
  /** browser mode: the normalised number to dial */
  dial?: string
}

export interface VoiceCallsPage {
  calls: VoiceCall[]
  next_cursor: string | null
}

export interface VoiceTranscriptTurn {
  /** agent/user: the AI agent's conversation; staff/guest: a transcribed outbound call */
  role: 'agent' | 'user' | 'staff' | 'guest'
  message: string | null
  tools: string[]
  /** seconds from the start of the recording (outbound transcripts) */
  start?: number
}

export interface VoiceCallDetail {
  call: Omit<VoiceCall, 'has_transcript'>
  events: { id: string; type: string; created_at: string }[]
  transcript: {
    conversation_id?: string
    agent_id?: string
    /** 'scribe' for a transcribed outbound recording */
    source?: string
    duration_secs: number | null
    transcript: VoiceTranscriptTurn[]
    text?: string
  } | null
}

const operatorApi = createApi({
  reducerPath: service,
  tagTypes: ['voice-numbers', 'voice-calls'],
  baseQuery: retry(axiosBaseQuery({ baseUrl: config.url.operator, service }), {
    maxRetries: 2,
  }),
  endpoints: (build) => ({
    getVoiceNumbers: build.query<
      { phone_numbers: VoicePhoneNumber[] },
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        url: `/voice/phone-numbers?organization_id=${org_id}&serial=${serial}`,
        method: 'get',
      }),
      providesTags: ['voice-numbers'],
    }),
    provisionVoiceNumber: build.mutation<
      { phone_number: VoicePhoneNumber },
      { org_id: string; serial: string; venue_name: string }
    >({
      query: ({ org_id, serial, venue_name }) => ({
        url: '/voice/phone-numbers/provision-new',
        method: 'post',
        data: { organization_id: org_id, serial, venue_name },
      }),
      invalidatesTags: ['voice-numbers'],
    }),
    getVoices: build.query<{ voices: VoiceCatalogueVoice[] }, void>({
      query: () => ({ url: '/voice/voices', method: 'get' }),
      keepUnusedDataFor: 3600,
    }),
    updateVoiceNumber: build.mutation<
      { phone_number: VoicePhoneNumber },
      { id: string; data: VoiceCallHandlingUpdate }
    >({
      query: ({ id, data }) => ({
        url: `/voice/phone-numbers/${id}`,
        method: 'patch',
        data,
      }),
      invalidatesTags: ['voice-numbers'],
    }),
    /**
     * Call volume and how much of it booked.
     *
     * The booking and money half is zion's /reporting/voice. Conversion is
     * worked out in the page from the two, because neither service can see the
     * other's database.
     */
    getVoiceCallStats: build.query<
      VoiceCallStats,
      { org_id: string; serial: string; start: string; end: string }
    >({
      query: ({ org_id, serial, start, end }) => ({
        url: `/voice/venues/${org_id}/${serial}/calls/stats?start=${start}&end=${end}`,
        method: 'get',
      }),
      providesTags: ['voice-calls'],
    }),
    getVoiceCalls: build.query<
      VoiceCallsPage,
      { org_id: string; serial: string; cursor?: string; limit?: number }
    >({
      query: ({ org_id, serial, cursor, limit = 25 }) => ({
        url: `/voice/venues/${org_id}/${serial}/calls?limit=${limit}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
        method: 'get',
      }),
      providesTags: ['voice-calls'],
    }),
    // Staff ringing a guest as the venue. The operator decides whether the
    // number may dial out, whether the destination is one a venue may ring,
    // and whether today's talk time is under the cap; a refusal comes back as
    // a plain sentence in `error`.
    placeOutboundCall: build.mutation<PlaceOutboundCallResult, PlaceOutboundCallInput>({
      query: ({ org_id, serial, ...rest }) => ({
        url: '/voice/calls/outbound',
        method: 'post',
        data: { organization_id: org_id, serial, ...rest },
      }),
      invalidatesTags: ['voice-calls'],
    }),
    // Polled by the call dialog while a ring-me call is up: Twilio's callbacks
    // move the row through ringing, answered, completed.
    getOutboundCall: build.query<{ call: VoiceCall }, { call_id: string }>({
      query: ({ call_id }) => ({ url: `/voice/calls/outbound/${call_id}`, method: 'get' }),
    }),
    hangupOutboundCall: build.mutation<{ ok: true }, { call_id: string }>({
      query: ({ call_id }) => ({ url: `/voice/calls/outbound/${call_id}/hangup`, method: 'post' }),
      invalidatesTags: ['voice-calls'],
    }),
    // Every call about one enquiry, newest first: what the file page lists.
    getEnquiryCalls: build.query<VoiceCallsPage, { org_id: string; enquiry_id: string }>({
      query: ({ org_id, enquiry_id }) => ({
        url: `/voice/calls?organization_id=${org_id}&enquiry_id=${encodeURIComponent(enquiry_id)}&limit=50`,
        method: 'get',
      }),
      providesTags: ['voice-calls'],
    }),
    getVoiceCall: build.query<VoiceCallDetail, { call_id: string }>({
      query: ({ call_id }) => ({
        url: `/voice/calls/${call_id}`,
        method: 'get',
      }),
    }),
    // The recording, proxied by the operator so the ElevenLabs workspace key
    // stays server side. Fetched as a blob rather than pointed at with an
    // <audio src>, because a bare element cannot carry the auth header and a
    // blob seeks locally without needing range requests.
    getVoiceCallAudio: build.query<Blob, { call_id: string }>({
      query: ({ call_id }) => ({
        url: `/voice/calls/${call_id}/audio`,
        method: 'get',
        responseType: 'blob',
      }),
    }),
  }),
})

export const {
  useGetVoicesQuery,
  useGetVoiceNumbersQuery,
  useProvisionVoiceNumberMutation,
  useUpdateVoiceNumberMutation,
  useGetVoiceCallsQuery,
  useGetVoiceCallStatsQuery,
  useGetVoiceCallQuery,
  useGetVoiceCallAudioQuery,
  useGetEnquiryCallsQuery,
  usePlaceOutboundCallMutation,
  useGetOutboundCallQuery,
  useHangupOutboundCallMutation,
} = operatorApi

export default operatorApi
